import { type FunctionComponent, useCallback, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import { useSearchContext } from "scmp-app/components/search/contexts";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { SearchEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { itemSearchExploreKeyAnglesItemArticleSearchResultStreamItemCategory$key } from "scmp-app/queries/__generated__/itemSearchExploreKeyAnglesItemArticleSearchResultStreamItemCategory.graphql";

import { SearchExploreKeyAnglesItemEntry } from "./entry";
import { isExpertPerspectiveArticle } from "./helpers";
import {
  Count,
  ExpertPerspectiveHeader,
  HeaderContainer,
  Index,
  StyledAccordion,
  StyledAccordionDetails,
  StyledAccordionSummary,
  StyledArrowIcon,
  Title,
  TitleContainer,
} from "./styles";

type Props = {
  expanded: boolean;
  index: number;
  onChange: (id: number | undefined) => void;
  reference: itemSearchExploreKeyAnglesItemArticleSearchResultStreamItemCategory$key;
};

export const SearchExploreKeyAnglesItem: FunctionComponent<Props> = ({
  expanded,
  index,
  onChange,
  reference,
}) => {
  const item = useFragment(
    graphql`
      fragment itemSearchExploreKeyAnglesItemArticleSearchResultStreamItemCategory on ArticleSearchResultStreamItemCategory {
        id
        name
        contents {
          entityId
          ...entityLink
          ...helpersExpertPerspectiveArticle
          ...entrySearchExploreKeyAnglesItemEntryContent
        }
      }
    `,
    reference,
  );

  const { currentValue } = useSearchContext();

  const handleChange = useCallback(
    (_: unknown, isExpanded: boolean) => {
      onChange?.(isExpanded ? index : undefined);
      if (!isExpanded) return;
      // GA4 search_expand_click
      sendGA4Tracking({
        action: "click",
        category: "search",
        customized_parameters: {
          search_text: currentValue,
          widget_category_name: item.name,
          widget_category_position: index + 1,
          widget_name: "Explore key angles",
        },
        subcategory: "expand",
      } as SearchEvent);
    },
    [currentValue, index, item.name, onChange],
  );

  const normalItems = useMemo(
    () => item.contents?.filter(content => content && !isExpertPerspectiveArticle(content)) ?? [],
    [item.contents],
  );

  const expertPerspectiveItems = useMemo(
    () => item.contents?.filter(content => content && isExpertPerspectiveArticle(content)) ?? [],
    [item.contents],
  );

  const resultCount = normalItems.length + expertPerspectiveItems.length;
  const resultText = resultCount > 1 ? "results" : "result";

  return (
    <StyledAccordion disableGutters elevation={0} expanded={expanded} onChange={handleChange}>
      <StyledAccordionSummary expandIcon={<StyledArrowIcon />} id={item.id}>
        <HeaderContainer>
          <Index>{index + 1}</Index>
          <TitleContainer>
            <Title>{item.name}</Title>
            <Count>
              [ <span>{resultCount}</span> {resultText} ]
            </Count>
          </TitleContainer>
        </HeaderContainer>
      </StyledAccordionSummary>
      <StyledAccordionDetails>
        {normalItems.map(
          (content, entryIndex) =>
            content && (
              <SearchExploreKeyAnglesItemEntry
                categoryName={item.name}
                categoryPosition={index + 1}
                key={content.entityId}
                reference={content}
                resultPosition={entryIndex + 1}
              />
            ),
        )}

        {notEmpty(expertPerspectiveItems) && (
          <>
            <ExpertPerspectiveHeader>Expert perspective</ExpertPerspectiveHeader>
            {expertPerspectiveItems.map(
              (content, entryIndex) =>
                content && (
                  <SearchExploreKeyAnglesItemEntry
                    categoryName={item.name}
                    categoryPosition={index + 1}
                    key={content.entityId}
                    reference={content}
                    resultPosition={normalItems.length + entryIndex + 1}
                  />
                ),
            )}
          </>
        )}
      </StyledAccordionDetails>
    </StyledAccordion>
  );
};

SearchExploreKeyAnglesItem.displayName = "SearchExploreKeyAnglesItem";
